<script lang="ts" setup>
import { onBeforeMount, ref } from "vue";
import {
  Field,
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { useRoute, useRouter } from "vue-router";
import ThreadPost from "@/components/mobile/post/ThreadPost.vue";
import { ThreadPostInstance } from "@/types/component";
import { useMate } from "@/stores/mate";
import { Mate } from "@/types/entity";
import { useMateCommit } from "@/stores/mateCommit";
import { useMedia } from "@/stores/media";
import IShapeIcon from "@/components/icon/iShapeIcon.vue";
import GlobalScrollLayout from "@/components/mobile/layout/GlobalScrollLayout.vue";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import i18n from "@/locales";
import { removeHtmlTag } from "@/tools/text";
// 内部状态
const router = useRouter();
const route = useRoute();
const mateStore = useMate();
const commitStore = useMateCommit();
const mateUuid = route.params.uuid.toString();
const threadPostRef = ref<ThreadPostInstance | null>(null);
const commitMsg = ref("");
const targetMate = ref<Mate | null>(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
// LifeCycle
const prepareData = async () => {
  isLoading.value = true;
  targetMate.value = await mateStore.getOrFetchMate(mateUuid);
  isLoading.value = false;
};
onBeforeMount(() => {
  prepareData();
});

// Event Handler
const onConfirm = async () => {
  const item = threadPostRef.value!.items[0];
  const { message, imgList, videoList, sharedMessageList } = item;
  if (
    removeHtmlTag(message) === "" &&
    imgList.filter((item) => item.status !== "failed").length === 0 &&
    videoList.filter((item) => item.status !== "failed").length === 0 &&
    sharedMessageList.length === 0
  ) {
    showFailToast(i18n.global.t("Message.nothingWasWritten"));
    return;
  }
  const mediaStore = useMedia();
  if (!mediaStore.validateMediaListElseToast(false, imgList, videoList)) {
    return;
  }

  const loadingToast = showLoadingToast({
    duration: 0,
    message: i18n.global.t("Message.publishing"),
  });
  try {
    isSubmitting.value = true;
    const mediaIdzz: number[] = [];
    if (imgList.length > 0) {
      mediaIdzz.push(...imgList.map((item) => item.mediaId));
    } else if (videoList.length > 0) {
      mediaIdzz.push(...videoList.map((item) => item.mediaId));
    }
    await commitStore.createMateCommit({
      mateId: mateStore.currentMate!.uuid,
      toMateId: mateUuid,
      title: commitMsg.value,
      text: message,
      mediaIdzz,
      messageIdzz: sharedMessageList.map((item) => item.id),
    });
    await showSuccessToast(i18n.global.t("Message.submissionSuccessful"));
    router.back();
  } catch (e) {
    showFailToast(i18n.global.t("Message.submissionFailed"));
    throw e;
  } finally {
    isSubmitting.value = false;
    loadingToast.close();
  }
};

const onCancel = () => {
  showConfirmDialog({
    title: i18n.global.t("ConfirmModal.cancelCreation"),
    message: i18n.global.t("ConfirmModal.cancelCreationText"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  })
    .then(() => {
      router.back();
    })
    .catch(() => {
      // do nothing
    });
};
</script>
<template>
  <global-scroll-layout>
    <template #nav>
      <threads-nav back-double-check show-back>
        <template #center>
          <div>{{ $t("Titles.createForTa") }}</div>
        </template>
        <template #right>
          <button
            :disabled="!commitMsg || isSubmitting"
            class="btn btn-sm btn-primary rounded"
            @click="onConfirm"
          >
            <span v-if="isSubmitting" class="loading"></span>
            {{ $t("Buttons.submit") }}
          </button>
        </template>
      </threads-nav>
    </template>
    <template #default>
      <div class="px-4">
        <div v-if="!isLoading" class="flex flex-col gap-2">
          <div class="flex flex-col gap-2">
            <div class="p-2 bg-info text-info-content rounded flex gap-1">
              <div class="h-[1.35rem] flex items-center">
                <iShapeIcon class="icon-sm" />
              </div>
              <div class="leading-normal">
                <span class="text-sm">{{ targetMate!.name }}</span>
                {{ $t("Texts.submittedCreationForTa") }}
              </div>
            </div>
            <field
              v-model="commitMsg"
              :placeholder="`${$t('Texts.submittedCreationPlaceholder', { mateName: '@' + targetMate!.name })}`"
              autosize
              class="textarea bg-neutral text-xs"
              rows="3"
              type="textarea"
            />
          </div>
          <thread-post
            ref="threadPostRef"
            :enable-mate-switch="false"
            :enable-multiple-moment="false"
            :enable-visibility-selection="false"
            :mate-uuid="mateUuid"
          />
        </div>
        <div v-else>
          <div
            class="flex flex-col items-center justify-center h-[200px] w-full"
          >
            <div class="flex flex-col items-center justify-center">
              <span class="loading"></span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </global-scroll-layout>
</template>
